import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { useUploadFile } from '@renderer/chat/hooks'
import { useReminderActions } from '../../../hooks/use-reminders'

export const isImageFile = (file: File): boolean => file.type.startsWith('image/')

/** The image files carried by a paste, if any. */
export function imageFilesFromClipboard(data: DataTransfer | null): Array<File> {
  const files: Array<File> = []
  for (const item of Array.from(data?.items ?? [])) {
    if (!item.type.startsWith('image/')) continue
    const file = item.getAsFile()
    if (file) files.push(file)
  }
  return files
}

/** Read one image off disk / the clipboard and upload it, yielding its file id. */
export async function uploadImageFile(upload: ReturnType<typeof useUploadFile>, file: File): Promise<string> {
  const data = new Uint8Array(await file.arrayBuffer())
  return upload({ name: file.name, mimeType: file.type, data })
}

/**
 * Attach images to an existing reminder. Non-images are skipped, and a failed
 * upload drops that one file rather than the batch — the reminder is already
 * saved, so a partial attach is the useful outcome.
 */
export function useAttachImagesToReminder(reminderId: string, onAttached: () => void) {
  const { appendAttachments } = useReminderActions()
  const uploadFile = useUploadFile()

  return useCallback(
    async (files: Iterable<File>) => {
      const uploadedIds: Array<string> = []
      for (const file of files) {
        if (!isImageFile(file)) continue
        try {
          uploadedIds.push(await uploadImageFile(uploadFile, file))
        } catch (error) {
          Sentry.captureException(error)
        }
      }

      if (uploadedIds.length === 0) return
      appendAttachments(reminderId, uploadedIds)
      onAttached()
    },
    [reminderId, appendAttachments, uploadFile, onAttached],
  )
}

export interface PendingImage {
  localId: string
  /** Object URL, so the thumbnail shows the picture while it uploads. */
  previewUrl: string
}

/**
 * Attaching images to a reminder from a detail surface: the picker, the files
 * in flight, and the attach itself. The pending ones are the host's to render
 * as uploading thumbnails, so a picked image appears at once rather than on
 * the round trip back.
 */
export function useReminderImageAttach(reminderId: string, onAttached: () => void) {
  const attachImages = useAttachImagesToReminder(reminderId, onAttached)
  const inputRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<Array<PendingImage>>([])

  const attach = useCallback(
    async (files: Array<File>) => {
      const images = files.filter(isImageFile)
      if (images.length === 0) return
      const batch: Array<PendingImage> = images.map((file) => ({
        localId: crypto.randomUUID(),
        previewUrl: URL.createObjectURL(file),
      }))
      setPending((current) => [...current, ...batch])
      try {
        await attachImages(images)
      } finally {
        setPending((current) => current.filter((image) => !batch.includes(image)))
        for (const { previewUrl } of batch) URL.revokeObjectURL(previewUrl)
      }
    },
    [attachImages],
  )

  // Object URLs outlive the component unless they are handed back.
  useEffect(
    () => () => {
      for (const { previewUrl } of pendingRef.current) URL.revokeObjectURL(previewUrl)
    },
    [],
  )
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  return {
    pending,
    attaching: pending.length > 0,
    openPicker: useCallback(() => inputRef.current?.click(), []),
    inputRef,
    onInputChange: useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        // Copied out before the reset: `input.files` is live, so clearing the
        // value empties the very list we are about to read.
        const files = Array.from(event.target.files ?? [])
        event.target.value = ''
        if (files.length > 0) await attach(files)
      },
      [attach],
    ),
  }
}
